import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  useBulkDeletePrivateLessonTemplates,
  useBulkTogglePrivateLessonTemplateStatus,
  useDeletePrivateLessonTemplate,
  usePrivateLessonTemplatesQuery,
} from "../api/queries";
import { PrivateLessonTemplatesFilter } from "../ui/PrivateLessonTemplatesFilter";
import { PrivateLessonTemplatesTable } from "../ui/PrivateLessonTemplatesTable";
import { PrivateLessonTemplatesToolbar } from "../ui/PrivateLessonTemplatesToolbar";

export function PrivateLessonTemplatesPage() {
  const nav = useNavigate({ from: "/app/private-lessons/templates" });
  const search: OffsetPageQueryDTO = useSearch({
    from: "/app/private-lessons/templates",
  });

  const list = usePrivateLessonTemplatesQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });

  const canManage = useActionPermission(["private-lessons:create"]);
  const deleteTemplate = useDeletePrivateLessonTemplate();
  const bulkDelete = useBulkDeletePrivateLessonTemplates();
  const bulkToggleStatus = useBulkTogglePrivateLessonTemplateStatus();

  const handleBulkDelete = async (ids: string[]) => {
    await toast.promise(bulkDelete.run(ids), {
      loading: "ກຳລັງລຶບ...",
      success: (result) =>
        `ລຶບ template ແພັກເກດຮຽນສ່ວນຕົວ ${result.deletedCount} ລາຍການສຳເລັດ`,
      error: "ລຶບ template ແພັກເກດຮຽນສ່ວນຕົວລົ້ມເຫຼວ",
    });
  };

  const handleBulkToggleStatus = async (
    ids: string[],
    status: "active" | "inactive",
  ) => {
    const statusLabel = status === "active" ? "ເປີດການໃຊ້ງານ" : "ປິດການໃຊ້ງານ";
    await toast.promise(bulkToggleStatus.run({ ids, status }), {
      loading: "ກຳລັງປ່ຽນສະຖານະ...",
      success: (result) =>
        `${statusLabel} template ແພັກເກດຮຽນສ່ວນຕົວ ${result.updatedCount} ລາຍການສຳເລັດ`,
      error: "ປ່ຽນສະຖານະ template ແພັກເກດຮຽນສ່ວນຕົວລົ້ມເຫຼວ",
    });
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "Template ແພັກເກດຮຽນສ່ວນຕົວ" },
          ]}
        />
      </Header>

      <Main>
        <PrivateLessonTemplatesToolbar
          canManage={!!canManage}
          onCreate={() => nav({ to: "/app/private-lessons/templates/create" })}
        />

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <PrivateLessonTemplatesFilter />

          <PrivateLessonTemplatesTable
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            sortBy={search.sort ? search.sort[0]?.field : undefined}
            sortOrder={
              search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onView={(template) =>
              nav({
                to: "/app/private-lessons/templates/$id",
                params: { id: template.id },
              })
            }
            onEdit={(template) =>
              nav({
                to: "/app/private-lessons/templates/$id/edit",
                params: { id: template.id },
              })
            }
            onDelete={async (id) =>
              toast.promise(deleteTemplate.run(id), {
                loading: "ກຳລັງລຶບ...",
                success: "ລຶບ template ແພັກເກດຮຽນສ່ວນຕົວສຳເລັດ",
                error: "ລຶບ template ແພັກເກດຮຽນສ່ວນຕົວລົ້ມເຫຼວ",
              })
            }
            onBulkDelete={handleBulkDelete}
            onBulkToggleStatus={handleBulkToggleStatus}
          />
        </div>
      </Main>
    </>
  );
}
